/**
 * 工作区状态管理 Store
 * 管理左侧工作区的显示内容（剧本、大纲、思维导图等）
 * 支持Notes系统（）
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_BASE_URL, getAuthHeaderValue } from '@/services/api';

export type WorkspaceViewType = 'document' | 'mindmap' | 'json' | 'outline' | 'script' | 'evaluation' | 'notes';

export interface WorkspaceContent {
  id: string;
  type: WorkspaceViewType;
  title: string;
  content: string;
  metadata?: {
    agentName?: string;
    agentId?: string;
    timestamp?: string;
    format?: string;
    [key: string]: any;
  };
}

// Note数据结构（与后端保持一致）
export interface Note {
  id: string;
  user_id: string;
  session_id: string;
  action: string;  // Agent类型
  name: string;    // Note名称（如character1）
  title?: string;  // 可选标题
  context: string; // Note内容
  select_status: number;  // 0未选择，1已选择
  user_comment?: string;  // 用户评论
  metadata?: any;
  created_at: string;
  updated_at: string;
}

const getWorkspaceApiUrl = (path: string) => new URL(`${API_BASE_URL}${path}`, window.location.origin).toString();

const getWorkspaceHeaders = (withJson = false): HeadersInit => {
  const authHeader = getAuthHeaderValue();
  return {
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    ...(authHeader ? { Authorization: authHeader } : {}),
  };
};

const downloadTextFile = (content: string, filename: string, type = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const placeholderNotesExport = (format: string) => (
  format === 'json'
    ? '[]'
    : '剧本创作 Notes 导出\n\n暂无可导出的 Notes，当前为工作台占位导出。'
);

interface WorkspaceState {
  // 当前内容
  currentContent: WorkspaceContent | null;
  viewMode: WorkspaceViewType;

  // 历史记录（用于撤销/重做）
  contentHistory: WorkspaceContent[];
  historyIndex: number;

  // Notes系统
  notes: Note[];  // 当前会话的所有Notes
  selectedNotes: Note[];  // 用户选择的Notes
  notesGroupedByAction: Record<string, Note[]>;  // 按Agent类型分组的Notes
  isLoadingNotes: boolean;

  // 工具栏状态
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;

  // Actions - 内容管理
  setContent: (content: WorkspaceContent) => void;
  updateContent: (updates: Partial<WorkspaceContent>) => void;
  clearContent: () => void;
  setViewMode: (mode: WorkspaceViewType) => void;

  // 历史操作
  undo: () => void;
  redo: () => void;

  // 工具栏操作
  saveContent: () => Promise<void>;
  copyContent: () => Promise<void>;
  exportContent: (format: string) => Promise<void>;

  // Actions - Notes管理
  loadNotes: (userId: string, sessionId: string, action?: string) => Promise<void>;
  toggleNoteSelection: (note: Note) => Promise<void>;
  updateNoteSelection: (note: Note, selected: boolean, comment?: string) => Promise<void>;
  getSelectedNotes: () => Note[];
  exportNotes: (format: string) => Promise<void>;
  exportSelectedNotes: () => Promise<void>;
  createNoteFromContent: (content: WorkspaceContent) => Promise<string | null>;
  updateNotesSelectionBulk: (notes: Note[], selected: boolean) => Promise<void>;

  // 内部方法
  _addToHistory: (content: WorkspaceContent) => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      currentContent: null,
      viewMode: 'document',
      contentHistory: [],
      historyIndex: -1,
      isDirty: false,
      canUndo: false,
      canRedo: false,

      // Notes初始状态
      notes: [],
      selectedNotes: [],
      notesGroupedByAction: {},
      isLoadingNotes: false,

      setContent: (content) => {
        set((state) => ({
          currentContent: content,
          viewMode: content.type,
          isDirty: false,
        }));
        get()._addToHistory(content);
      },

      updateContent: (updates) => {
        set((state) => {
          if (!state.currentContent) return state;
          const updated = { ...state.currentContent, ...updates };
          return {
            currentContent: updated,
            isDirty: true,
          };
        });
      },

      clearContent: () => {
        set({
          currentContent: null,
          isDirty: false,
        });
      },

      setViewMode: (mode) => {
        set({ viewMode: mode });
      },

      undo: () => {
        set((state) => {
          if (state.historyIndex <= 0) return state;
          const newIndex = state.historyIndex - 1;
          const content = state.contentHistory[newIndex];
          return {
            ...state,
            currentContent: content,
            historyIndex: newIndex,
            canUndo: newIndex > 0,
            canRedo: newIndex < state.contentHistory.length - 1,
            isDirty: true,
          };
        });
      },

      redo: () => {
        set((state) => {
          if (state.historyIndex >= state.contentHistory.length - 1) return state;
          const newIndex = state.historyIndex + 1;
          const content = state.contentHistory[newIndex];
          return {
            ...state,
            currentContent: content,
            historyIndex: newIndex,
            canUndo: newIndex > 0,
            canRedo: newIndex < state.contentHistory.length - 1,
            isDirty: true,
          };
        });
      },

      saveContent: async () => {
        const { currentContent } = get();
        if (!currentContent) return;

        try {
          const storageKey = `juben_workspace_content_${currentContent.id}`;
          localStorage.setItem(storageKey, JSON.stringify(currentContent));
          set({ isDirty: false });
        } catch (error) {
          set({ isDirty: true });
        }
      },

      copyContent: async () => {
        const { currentContent } = get();
        if (!currentContent) return;

        try {
          await navigator.clipboard.writeText(currentContent.content);
        } catch (error) {
          const storageKey = `juben_workspace_clipboard_${currentContent.id}`;
          try {
            localStorage.setItem(storageKey, currentContent.content);
          } catch {
            // Clipboard and localStorage can both be unavailable in strict browser modes.
          }
        }
      },

      exportContent: async (format: string) => {
        const { currentContent } = get();
        if (!currentContent) return;

        try {
          downloadTextFile(currentContent.content, `${currentContent.title}.${format}`);
        } catch (error) {
          // Export is a local convenience action; keep the workspace usable if the browser blocks downloads.
        }
      },

      // Notes管理功能
      loadNotes: async (userId: string, sessionId: string, action?: string) => {
        set({ isLoadingNotes: true });
        try {
          const params = new URLSearchParams({
            user_id: userId,
            session_id: sessionId,
          });
          if (action) params.append('action', action);

          const response = await fetch(getWorkspaceApiUrl(`/juben/notes/list?${params.toString()}`), {
            headers: getWorkspaceHeaders(true),
          });
          const raw = await response.text();

          let data: any = null;
          try {
            data = raw ? JSON.parse(raw) : null;
          } catch (parseError) {
            set({ notes: [], notesGroupedByAction: {}, isLoadingNotes: false });
            return;
          }

          if (response.ok && data && data.success !== false) {
            set({
              notes: data.notes || data.data?.notes || [],
              notesGroupedByAction: data.grouped_by_action || data.data?.grouped_by_action || {},
              isLoadingNotes: false,
            });
          } else {
            set({ notes: [], notesGroupedByAction: {}, isLoadingNotes: false });
          }
        } catch (error) {
          set({ notes: [], notesGroupedByAction: {}, isLoadingNotes: false });
        }
      },

      toggleNoteSelection: async (note: Note) => {
        const newSelected = !note.select_status;
        await get().updateNoteSelection(note, newSelected);
      },

      updateNoteSelection: async (note: Note, selected: boolean, comment?: string) => {
        try {
          await fetch(getWorkspaceApiUrl('/juben/notes/batch-select'), {
            method: 'POST',
            headers: getWorkspaceHeaders(true),
            body: JSON.stringify({
              user_id: note.user_id,
              session_id: note.session_id,
              selections: [{
                action: note.action,
                name: note.name,
                selected: selected,
                user_comment: comment || note.user_comment,
              }],
            }),
          });

          // 更新本地状态
          set((state) => ({
            notes: state.notes.map(n =>
              n.id === note.id
                ? { ...n, select_status: selected ? 1 : 0, user_comment: comment || n.user_comment }
                : n
            ),
          }));

          // 重新加载已选择的Notes
          await get().loadNotes(note.user_id, note.session_id);
        } catch (error) {
          set((state) => ({
            notes: state.notes.map(n =>
              n.id === note.id
                ? { ...n, select_status: selected ? 1 : 0, user_comment: comment || n.user_comment }
                : n
            ),
          }));
        }
      },

      getSelectedNotes: () => {
        const { notes } = get();
        return notes.filter(n => n.select_status === 1);
      },

      exportNotes: async (format: string) => {
        const { notes } = get();
        if (notes.length === 0) return;

        try {
          // 获取用户ID和会话ID（从第一个note或localStorage）
          const userId = notes[0]?.user_id || localStorage.getItem('userId') || 'default_user';
          const sessionId = notes[0]?.session_id || localStorage.getItem('sessionId') || 'default_session';

          const response = await fetch(getWorkspaceApiUrl('/juben/notes/export'), {
            method: 'POST',
            headers: getWorkspaceHeaders(true),
            body: JSON.stringify({
              user_id: userId,
              session_id: sessionId,
              export_format: format,
              include_user_comments: true,
            }),
          });

          const data = await response.json();

          if (response.ok && data.success !== false) {
            downloadTextFile(data.exported_data || placeholderNotesExport(format), data.filename || `workspace_notes.${format}`);
          } else {
            downloadTextFile(placeholderNotesExport(format), `workspace_notes_placeholder.${format}`);
          }
        } catch (error) {
          downloadTextFile(placeholderNotesExport(format), `workspace_notes_placeholder.${format}`);
        }
      },

      exportSelectedNotes: async () => {
        const { notes } = get();
        const selected = notes.filter(n => n.select_status === 1);
        if (selected.length === 0) return;

        const exportText = selected
          .map((note, index) => {
            const title = note.title || note.name || `Note ${index + 1}`;
            return `# ${title}\n${note.context}\n`;
          })
          .join('\n');

        try {
          downloadTextFile(exportText, `selected_notes_${new Date().toISOString().slice(0, 10)}.txt`);
        } catch (error) {
          // Browser download can be blocked; keep the workspace state unchanged.
        }
      },

      createNoteFromContent: async (content: WorkspaceContent) => {
        try {
          const userId = localStorage.getItem('userId') || 'default_user';
          const sessionId = localStorage.getItem('sessionId') || 'default_session';
          const action = content.metadata?.agentId || 'document';
          const name = `doc_${Date.now()}`;

          const response = await fetch(getWorkspaceApiUrl('/juben/notes/create'), {
            method: 'POST',
            headers: getWorkspaceHeaders(true),
            body: JSON.stringify({
              user_id: userId,
              session_id: sessionId,
              action,
              name,
              context: content.content,
              title: content.title,
              content_type: 'document',
              metadata: content.metadata || {},
            }),
          });

          const raw = await response.text();
          let data: any = null;
          if (raw) {
            try {
              data = JSON.parse(raw);
            } catch {
              data = null;
            }
          }

          if (!response.ok) {
            return null;
          }

          if (data?.success) {
            await get().loadNotes(userId, sessionId);
            return data?.data?.note_id || null;
          }
          return null;
        } catch (error) {
          return null;
        }
      },

      updateNotesSelectionBulk: async (notesList: Note[], selected: boolean) => {
        if (notesList.length === 0) return;
        const userId = notesList[0]?.user_id || localStorage.getItem('userId') || 'default_user';
        const sessionId = notesList[0]?.session_id || localStorage.getItem('sessionId') || 'default_session';
        try {
          await fetch(getWorkspaceApiUrl('/juben/notes/batch-select'), {
            method: 'POST',
            headers: getWorkspaceHeaders(true),
            body: JSON.stringify({
              user_id: userId,
              session_id: sessionId,
              selections: notesList.map((note) => ({
                action: note.action,
                name: note.name,
                selected,
                user_comment: note.user_comment,
              })),
            }),
          });

          set((state) => ({
            notes: state.notes.map(n =>
              notesList.some(target => target.id === n.id)
                ? { ...n, select_status: selected ? 1 : 0 }
                : n
            ),
          }));

          await get().loadNotes(userId, sessionId);
        } catch (error) {
          set((state) => ({
            notes: state.notes.map(n =>
              notesList.some(target => target.id === n.id)
                ? { ...n, select_status: selected ? 1 : 0 }
                : n
            ),
          }));
        }
      },

      _addToHistory: (content) => {
        set((state) => {
          // 移除当前索引之后的所有历史记录
          const newHistory = state.contentHistory.slice(0, state.historyIndex + 1);
          newHistory.push(content);

          // 限制历史记录数量
          if (newHistory.length > 50) {
            newHistory.shift();
          }

          const newIndex = newHistory.length - 1;

          return {
            contentHistory: newHistory,
            historyIndex: newIndex,
            canUndo: newIndex > 0,
            canRedo: false,
          };
        });
      },
    }),
    { name: 'WorkspaceStore' }
  )
);
